import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Badge,
  Card,
  Checkbox,
  ConfirmModal,
  Modal,
  Page,
  SimpleSelect,
  Skeleton,
  SupportingText,
  TextInput,
} from 'corso-admin-ui';
import {
  CrewClaimTypeEnum,
  crewClaimTypeEnumPluralizedName,
  CrewMerchantUi,
} from 'corso-types';
import { formatter } from 'corso-web-lib';
import { ChangeEvent, FormEventHandler, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from 'react-hook-form';
import Separator from '~/components/Separator';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import { useProductGroups } from '~/features/settings/hooks/useProductGroups';
import { useClaimReasons, useClaimReasonUpsert } from '~/hooks/useClaimReasons';
import { ClaimReason, ClaimReasonGroup, UpsertFormProps } from '~/types';
import {
  useClaimReasonGroupDelete,
  useClaimReasonGroups,
  useClaimReasonGroupUpsert,
} from './hooks/useClaimReasonGroups';
import ReasonList from './ReasonList';
import { ConfigurationAlert } from '~/features/settings/components/ConfigurationAlert';

const claimReasonGroupUpdate = CrewMerchantUi.claimReasonGroupUpdateSchema;
type ClaimReasonGroupUpdate = CrewMerchantUi.ClaimReasonGroupUpdate;
const claimReasonGroupCreate = CrewMerchantUi.claimReasonGroupCreateSchema;
type ClaimReasonGroupCreate = CrewMerchantUi.ClaimReasonGroupCreate;

// TODO: do not allow creation of reasons for right to withdraw

function ClaimReasonCheckbox({ reason }: { reason: ClaimReason }) {
  const { control } = useFormContext<ClaimReasonGroup>();
  const {
    field: { onChange, value: selectedReasons },
  } = useController({
    control,
    name: 'claimReasons',
  });

  const checked = useMemo(
    () => selectedReasons.some((r) => r.id === reason.id),
    [selectedReasons, reason],
  );
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(
      event.target.checked
        ? [...selectedReasons, reason]
        : selectedReasons.filter((r) => r.id !== reason.id),
    );
  };
  return (
    <Checkbox
      className="ml-auto h-6 w-6"
      value={reason.name}
      disabled={reason.category.code === 'Right_To_Withdraw'}
      checked={checked}
      onChange={handleChange}
      aria-label={reason.name} // visually the reason list list item is providing the label, but don't access to it here
    />
  );
}

function ClaimReasonGroupForm({
  show: showForm,
  values: group,
  onSubmit,
  onClose,
}: UpsertFormProps<ClaimReasonGroupCreate, ClaimReasonGroupUpdate>) {
  const isDefaultReasonGroup = group?.isDefault ?? false;
  const { data: reasons = [] } = useClaimReasons();
  const { data: productGroups } = useProductGroups();

  const methods = useForm({
    resolver: zodResolver(claimReasonGroupUpdate.or(claimReasonGroupCreate)),
    defaultValues: {
      claimReasons: [],
      isDefault: false,
    },
    values: group ?? undefined,
  });

  const submitHandler: FormEventHandler = (event) => {
    methods
      .handleSubmit((values) => {
        onSubmit(values);
        methods.reset();
      })(event)
      .catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      title={group ? group.name : 'Add New Group'}
      description="Select the reasons that should be included in this group."
      onClose={onClose}
    >
      <FormProvider {...methods}>
        <form className="flex flex-col gap-4" onSubmit={submitHandler}>
          {!isDefaultReasonGroup && (
            <>
              <TextInput
                id="name"
                label="Group Name"
                required
                {...methods.register('name')}
                error={methods.formState.errors.name?.message}
              />

              <Separator />

              <Controller
                name="claimType"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                  <SimpleSelect
                    label="Claim Type"
                    options={Object.values(CrewClaimTypeEnum).map(
                      (claimType) => ({
                        label: crewClaimTypeEnumPluralizedName[claimType],
                        value: claimType,
                      }),
                    )}
                    value={value}
                    onChange={onChange}
                    details="What claim type should this reason group be applied to?"
                    error={methods.formState.errors.claimType?.message}
                  />
                )}
              />

              <Separator />

              <Controller
                name="storeProductGroup"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                  <SimpleSelect
                    label="Product Group"
                    options={productGroups.map(({ id, name }) => ({
                      label: name,
                      value: `${id}`,
                    }))}
                    value={`${value?.id ?? ''}`} // always controlled, even if none selected
                    onChange={(selected) => {
                      const selectedId = Number.parseInt(selected, 10);
                      onChange(
                        productGroups.find(({ id }) => id === selectedId),
                      );
                    }}
                    details={
                      <p>
                        Select the product group that this reason group should
                        be applied to, groups can be configured{' '}
                        <Action variant="link" to="../product-groups">
                          here
                        </Action>
                        .
                      </p>
                    }
                    error={methods.formState.errors.storeProductGroup?.message}
                  />
                )}
              />
            </>
          )}
          <Separator />
          <ReasonList
            dataTestId="reason-list-select"
            // TODO consider alternative max height
            className="max-h-104 overflow-y-auto"
            reasons={reasons}
            action={ClaimReasonCheckbox}
          />
          <SupportingText error>
            {methods.formState.errors.claimReasons?.message}
          </SupportingText>

          <Action variant="primary" type="submit">
            Save
          </Action>
        </form>
      </FormProvider>
    </Modal>
  );
}

function DeleteGroupAction({ group }: { group: ClaimReasonGroup }) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutate: deleteClaimReasonGroup, isPending } =
    useClaimReasonGroupDelete();

  const closeDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
  };

  const openConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  const onConfirm = () => {
    deleteClaimReasonGroup(group);
    closeDeleteConfirmation();
  };

  return (
    <div>
      <Action
        icon="remove"
        accessibilityLabel={`Delete ${group.name} group`}
        onClick={openConfirmation}
        variant="ghost"
      />
      <ConfirmModal
        title="Delete Group"
        prompt={`Are you sure you want to delete ${group.name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        show={showDeleteConfirmation}
        loading={isPending}
        onConfirm={onConfirm}
        onCancel={closeDeleteConfirmation}
      />
    </div>
  );
}

function EditGroupAction({ group }: { group: ClaimReasonGroup }) {
  const [showForm, setShowForm] = useState(false);
  const { mutate: upsertClaimReasonGroup } = useClaimReasonGroupUpsert();

  const closeForm = () => {
    setShowForm(false);
  };

  const openForm = () => {
    setShowForm(true);
  };

  return (
    <div>
      <Action
        accessibilityLabel={`Edit ${group.name} group`}
        icon="edit"
        onClick={openForm}
        variant="ghost"
      />
      <ClaimReasonGroupForm
        show={showForm}
        values={group}
        onSubmit={(upsert) => {
          upsertClaimReasonGroup(upsert);
          closeForm();
        }}
        onClose={closeForm}
      />
    </div>
  );
}

function ReasonGroupDisplay({ group }: { group: ClaimReasonGroup }) {
  const [showReasonGroup, setShowReasonGroup] = useState(false);

  return (
    <>
      <Action
        icon="eye"
        accessibilityLabel={`View reasons in ${group.name}`}
        onClick={() => setShowReasonGroup(true)}
        variant="ghost"
      />
      <Modal
        onClose={() => setShowReasonGroup(false)}
        show={showReasonGroup}
        title={group.name}
      >
        <ReasonList reasons={group.claimReasons} />
      </Modal>
    </>
  );
}

const englishReasonCountPlurals: Readonly<Record<Intl.LDMLPluralRule, string>> =
  {
    zero: 'reasons',
    one: 'reason',
    two: 'reasons',
    few: 'reasons',
    many: 'reasons',
    other: 'reasons',
  } as const;
function ClaimReasonGroupDisplay({ group }: { group: ClaimReasonGroup }) {
  return (
    <li className="flex justify-between font-medium">
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm">{group.name}</span>
          <Badge>{group.claimType}</Badge>
        </div>
        <div className="mt-1 text-xs font-normal text-neutral-500">
          {`${group.claimReasons.length} ${formatter.plural(
            group.claimReasons.length,
            englishReasonCountPlurals,
            'en',
          )} selected`}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ReasonGroupDisplay group={group} />
        <EditGroupAction group={group} />
        {!group.isDefault && <DeleteGroupAction group={group} />}
      </div>
    </li>
  );
}

function ReasonGroupSkeleton() {
  return (
    <div className="flex flex-row items-center justify-between gap-2">
      <Skeleton.Rectangle width="100%" height="45px" />
      <Skeleton.Rectangle width="45px" height="45px" />
      <Skeleton.Rectangle width="45px" height="45px" />
    </div>
  );
}

function ReasonListSkeleton() {
  return <Skeleton.Rectangle width="100%" height="52px" />;
}

export default function ClaimReasonSettingsPage() {
  const { data: claimReasons = [], isLoading: areReasonsLoading } =
    useClaimReasons();
  const { mutate: saveClaimReason } = useClaimReasonUpsert();
  const { data: groups = [], isLoading: areGroupsLoading } =
    useClaimReasonGroups();

  const [showForm, setShowForm] = useState(false);

  const { mutate: upsertClaimReasonGroup } = useClaimReasonGroupUpsert();
  const { data: productGroups } = useProductGroups();
  const canCreateReasonGroup = productGroups.length > 0;

  const closeForm = () => setShowForm(false);

  return (
    <Page title="Reasons">
      <div className="flex flex-col gap-4">
        <SettingsAnchorCard id="reason-groups">
          <Card.Line>
            <Card.Heading as="h2">Reason Groups</Card.Heading>
            <Action
              icon="add"
              onClick={() => setShowForm(true)}
              variant="ghost"
              accessibilityLabel="Add New Group"
              disabled={!canCreateReasonGroup}
            />
          </Card.Line>

          <Card.Description>
            Reason Groups are used to organize which reasons are offered to
            customers during the Return or Warranty claim process.
          </Card.Description>
          <Skeleton
            instances={1}
            skeleton={ReasonGroupSkeleton}
            isLoading={areGroupsLoading}
          >
            <ul data-testid="reason-group-list" className="space-y-4">
              {groups.map((group) => (
                <ClaimReasonGroupDisplay key={group.id} group={group} />
              ))}
            </ul>

            {!canCreateReasonGroup && (
              <ConfigurationAlert
                variant="info"
                heading="Product Group Required"
                message="You must create a Product Group before you can create a new Reason Group."
                to="../product-groups"
              />
            )}
            <ClaimReasonGroupForm
              show={showForm}
              onSubmit={(upsert) => {
                upsertClaimReasonGroup(upsert);
                closeForm();
              }}
              onClose={closeForm}
            />
          </Skeleton>
        </SettingsAnchorCard>
        <SettingsAnchorCard id="reasons">
          <Card.Heading as="h2">Reasons</Card.Heading>
          <Card.Description>
            Add or remove reasons from the above groups to offer as options
            during the claim process, the same reason can be added to multiple
            groups.
          </Card.Description>
          <Skeleton
            instances={1}
            skeleton={ReasonListSkeleton}
            isLoading={areReasonsLoading}
          >
            <ReasonList
              dataTestId="editable-reason-list"
              editable
              reasons={claimReasons}
              onChange={saveClaimReason}
            />
          </Skeleton>
        </SettingsAnchorCard>
      </div>
    </Page>
  );
}
